import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { Profile } from '../../models/profile';
import { UserLogicService } from '../../services/user.logic.service';
import { EmailService } from '../../services/email.service';
import { ActiveIngredientApiService } from '../../services/echo/active-ingredient.api.service';
import { EEAOutputProjectSummaryResultSurfaceWaterApiService, EraSmeSelectionStatus } from '../../services/out-put-project/eea-output-project-step1n2-summary-result.api.service';
import { Constants } from '../../utils/constants';
import { Project } from '../../models/project';
import { ERAPecEccAquaticLogicService } from 'src/app/environmental-risk-assessment/era-pec-ecc/era-pec-ecc-aquatic/services/era-pec-ecc-aquatic.logic.service';
import { EraPecEccAquaticEu } from 'src/app/environmental-risk-assessment/era-pec-ecc/era-pec-ecc-aquatic/era-pec-ecc-aquatic.model';

interface StepStatus {
  label: string;
  selected: boolean;
  errorMessage?: string;
}

@Component({
  selector: 'sme-notification',
  templateUrl: './sme-notification.component.html',
  styleUrls: ['./sme-notification.component.css'],
  providers: [MessageService]
})
export class SmeNotificationComponent implements OnInit {
  @Input() selectedProject?: Project;
  @Output() onCloseEvent = new EventEmitter<void>();

  visible: boolean = true;
  suggestions: Profile[] = [];
  selectedRecipients: Profile[] = [];
  notes: string = '';
  sending: boolean = false;
  pecEccAquaticData: EraPecEccAquaticEu[] = [];
  private moleculeNamesByPk = new Map<number, string>();

  stepStatuses: StepStatus[] = [
    { label: Constants.SCENARIO_FOCUS.STEP1, selected: false },
    { label: Constants.SCENARIO_FOCUS.STEP2, selected: false },
    { label: Constants.SCENARIO_FOCUS.STEP3, selected: false },
    { label: Constants.SCENARIO_FOCUS.STEP4, selected: false }
  ];

  constructor(
    private userLogicService: UserLogicService,
    private surfaceWaterApiService: EEAOutputProjectSummaryResultSurfaceWaterApiService,
    private emailService: EmailService,
    private messageService: MessageService,
    private pecAquaticLogicService : ERAPecEccAquaticLogicService,
    private activeIngredientApiService: ActiveIngredientApiService
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.selectedProject?.projectPk) {
      this.loadEraSmeSelectionStatus();
      this.pecEccAquaticData = (await this.pecAquaticLogicService.getPecEccAquaticEuByProjectPk(this.selectedProject.projectPk))
        .filter(item => item.eraSmeSelection);
      await this.loadActiveIngredientNames();
    }
  }

  private async loadActiveIngredientNames(): Promise<void> {
    const moleculePks = [...new Set((this.selectedProject?.projectXCompoundXModel ?? [])
      .filter(compound => !compound.MetabolitePk)
      .map(compound => compound.MoleculePk)
      .filter((moleculePk): moleculePk is number => moleculePk != undefined))];

    if (moleculePks.length === 0) return;

    const molecules = await firstValueFrom(this.activeIngredientApiService.getMoleculeByMoleculePks(moleculePks));
    this.moleculeNamesByPk = new Map(molecules
      .filter(molecule => molecule.moleculePk != undefined && !!molecule.moleculeName)
      .map(molecule => [molecule.moleculePk!, molecule.moleculeName!]));
  }

  private loadEraSmeSelectionStatus(): void {
    this.surfaceWaterApiService.getEraSmeSelectionStatus(this.selectedProject!.projectPk).subscribe({
      next: (statuses: EraSmeSelectionStatus[]) => {
        const selectedSteps = new Set(statuses.map(s => s.scenarioFocus));
        this.stepStatuses.forEach(step => step.selected = selectedSteps.has(step.label));
        this.applyStepDependencyValidation();
      },
      error: () => {}
    });
  }

  private applyStepDependencyValidation(): void {
    const step3 = this.stepStatuses.find(s => s.label === Constants.SCENARIO_FOCUS.STEP3);
    const step4 = this.stepStatuses.find(s => s.label === Constants.SCENARIO_FOCUS.STEP4);
    if (step3 && step4 && step4.selected && !step3.selected) {
      step3.errorMessage = 'You must save Step3 PECs before send Step4';
    } else if (step3) {
      step3.errorMessage = undefined;
    }
  }

  searchUsers(event: AutoCompleteCompleteEvent): void {
    this.userLogicService.searchUsers(event.query).subscribe({
      next: (users) => this.suggestions = users,
      error: () => this.suggestions = []
    });
  }

  get isSendDisabled(): boolean {
    const hasSelectedStep = this.stepStatuses.some(step => step.selected);
    const hasStepError = this.stepStatuses.some(step => !!step.errorMessage);
    return this.selectedRecipients.length === 0 || !hasSelectedStep || hasStepError || this.sending;
  }

  send(): void {
    const recipients = this.selectedRecipients.map(r => r.mail).filter((mail): mail is string => !!mail);
    if (recipients.length === 0) {
      return;
    }

    this.sending = true;
    const subject = `BaMS notification: the PECs values in Project ${this.selectedProject?.name} have been approved`;
    const body = this.buildEmailBody();

    this.emailService.sendMail(recipients, subject, body).subscribe({
      next: () => {
        this.sending = false;
        this.messageService.add({ severity: 'success', summary: 'Notification sent', detail: 'The SME notification email was sent successfully.' });
        this.close();
      },
      error: () => {
        this.sending = false;
        this.messageService.add({ severity: 'error', summary: 'Send failed', detail: 'The SME notification email could not be sent.' });
      }
    });
  }

  // Inline CSS is required since most email clients strip external/embedded stylesheets
  private static readonly EMAIL_STYLES = {
    container: 'font-family: Arial, Helvetica, sans-serif; color: #333333; font-size: 12px; max-width: 700px;',
    heading: 'font-size: 13px; color: #333333; border-bottom: 2px solid #F59E0B; padding-bottom: 4px; margin: 16px 0 8px;',
    paragraph: 'margin: 0 0 12px; line-height: 1.5;',
    notesBox: 'background-color: #fff8e1; border-left: 4px solid #F59E0B; padding: 8px 12px; margin-bottom: 12px;',
    tableWrapper: 'overflow-x: auto; margin-bottom: 12px;',
    table: 'width: auto; border-collapse: collapse; font-size: 11px; table-layout: auto;',
    th: 'background-color: #F59E0B; color: #333333; text-align: left; padding: 5px; border: 1px solid #dddddd; white-space: nowrap;',
    td: 'padding: 5px; border: 1px solid #dddddd; word-break: break-word; white-space: nowrap;'
  };

  private buildEmailBody(): string {
    const styles = SmeNotificationComponent.EMAIL_STYLES;
    const notesHtml = this.notes
      ? `<div style="${styles.notesBox}"><p style="margin: 0 0 6px; font-weight: bold;">Notes</p><p style="margin: 0;">${this.notes}</p></div>`
      : '';
    const activesTable = this.buildActiveIngredientsModelsTable();
    const maxPecsTables = this.buildMaxPecsDetailsTables();
    return `
      <div style="${styles.container}">
        ${notesHtml}
        <h3 style="${styles.heading}">Project details</h3>
        <p style="${styles.paragraph}">
          <strong>Project number:</strong> ${this.selectedProject?.projectPk ?? ''}<br>
          <strong>Project name:</strong> ${this.selectedProject?.name ?? ''}<br>
          <strong>Project description:</strong> ${this.selectedProject?.description ?? ''}<br>
          <strong>Formulation:</strong> ${this.selectedProject?.formulation ?? ''}
        </p>
        ${activesTable}
        ${maxPecsTables}
      </div>`;
  }

  private renderTable(title: string, headers: string[], rows: string[]): string {
    const { heading, tableWrapper, table, th } = SmeNotificationComponent.EMAIL_STYLES;
    const headerCells = headers.map(header => `<th style="${th}">${header}</th>`).join('');
    return `
      <h3 style="${heading}">${title}</h3>
      <div style="${tableWrapper}">
        <table style="${table}">
          <tr>${headerCells}</tr>
          ${rows.join('')}
        </table>
      </div>`;
  }

  private buildActiveIngredientsModelsTable(): string {
    const activeIngredients = (this.selectedProject?.projectXCompoundXModel ?? [])
      .filter(c => !c.MetabolitePk);

    const modelsByActive = new Map<string, Set<string>>();
    activeIngredients.forEach(c => {
      const activeName = c.AlternativeNameCompound || (c.MoleculePk != undefined ? this.moleculeNamesByPk.get(c.MoleculePk) : undefined);
      if (!activeName) {
        return;
      }
      if (!modelsByActive.has(activeName)) {
        modelsByActive.set(activeName, new Set<string>());
      }
      if (c.ModelName) {
        modelsByActive.get(activeName)!.add(c.ModelName);
      }
    });

    const { td } = SmeNotificationComponent.EMAIL_STYLES;
    const rows = [...modelsByActive.entries()]
      .map(([activeName, models]) => `<tr><td style="${td}">${activeName}</td><td style="${td}">${[...models].join(', ')}</td></tr>`);

    return this.renderTable('Active ingredients & models', ['Active', 'Models'], rows);
  }

  private buildMaxPecsDetailsTables(): string {
    const rowsByScheme = this.groupStepRowsByApplicationScheme();
    return [...rowsByScheme.entries()]
      .map(([scheme, rows]) => this.renderTable(`Max PECs details / Application scheme: ${scheme}`, ['Step', 'Detail', 'Max PECsw', 'Active', 'Dataset'], rows))
      .join('');
  }

  private groupStepRowsByApplicationScheme(): Map<string, string[]> {
    const { td } = SmeNotificationComponent.EMAIL_STYLES;
    const stepOrder = this.stepStatuses.map(step => step.label);
    const items = this.pecEccAquaticData
      .filter(item => item.scenarioFocus && stepOrder.includes(item.scenarioFocus))
      .filter(item => item.scenarioFocus !== Constants.SCENARIO_FOCUS.STEP2 || (item as any).region !== 'No Run-off/drainage')
      .sort((a, b) => stepOrder.indexOf(a.scenarioFocus!) - stepOrder.indexOf(b.scenarioFocus!));

    const rowsByScheme = new Map<string, string[]>();
    items.forEach(item => {
      const scheme = item.applicationScheme ?? '';
      if (!rowsByScheme.has(scheme)) {
        rowsByScheme.set(scheme, []);
      }
      rowsByScheme.get(scheme)!.push(`<tr>
        <td style="${td}">${item.scenarioFocus}</td>
        <td style="${td}">${this.buildStepDetail(item.scenarioFocus!, item)}</td>
        <td style="${td}">${item.maxPecSw ?? ''}</td>
        <td style="${td}">${item.substance ?? ''}</td>
        <td style="${td}">${item.dataSet ?? ''}</td>
      </tr>`);
    });
    return rowsByScheme;
  }

  private buildStepDetail(scenarioFocus: string, item: EraPecEccAquaticEu): string {
    const scenarioWaterbody = `${item.scenarios ?? ''} ${item.waterbody ?? ''}`.trim();
    switch (scenarioFocus) {
      case Constants.SCENARIO_FOCUS.STEP2:
        return (item as any).region ?? '';
      case Constants.SCENARIO_FOCUS.STEP3:
        return scenarioWaterbody;
      case Constants.SCENARIO_FOCUS.STEP4:
        return `${scenarioWaterbody}: Nozzle reduction = ${item.nozzleDriftReduction ?? 'None'}, Vegetative strip = ${item.bufferZone ?? 'None'}, Buffer zone = ${item.bufferDistance ?? 'None'}`;
      default:
        return '';
    }
  }

  close(): void {
    this.onCloseEvent.emit();
  }
}
